import { Grid2, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { type AxisConfig, BarChart, type ChartsXAxisProps } from '@mui/x-charts';
import { type FC, useEffect, useRef, useState } from 'react';
import type { IIsPdf, TotalSavedPerVisitData } from '../../models';
import { AvaniLightTheme } from '../../theme/schemes/AvaniLightTheme';
import { readableDateAndHour } from '../../utils';

interface AvaniTotalWastageChartProps extends IIsPdf {
  data: TotalSavedPerVisitData[];
  white?: boolean;
  sortBy: string;
}

const Mask = styled('div')(() => ({
  height: '245px',
  overflow: 'hidden'
}));

const customChartSeriesColor = '#d1eded';

export const AvaniTotalWastageChart: FC<AvaniTotalWastageChartProps> = ({ data, sortBy, white, isPDF = false }) => {
  const theme = isPDF ? AvaniLightTheme : useTheme();
  const chartColors = [customChartSeriesColor, theme.colors.avani.teal];

  const formattedDates = data.map(({ startedAt }, index) =>
    index === 0 ? 'Current Visit' : readableDateAndHour(startedAt)
  );

  const MaskEl = useRef<HTMLDivElement>(null);

  const [maskWidth, setMaskWidth] = useState(0);
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0]) {
        setMaskWidth(entries[0].contentRect.width); // Update width dynamically
      }
    });

    if (MaskEl.current) {
      resizeObserver.observe(MaskEl.current);
    }

    return () => {
      if (MaskEl.current) {
        resizeObserver.unobserve(MaskEl.current);
      }
    };
  }, []);

  return (
    <div
      style={{
        display: 'Grid2',
        gridTemplateColumns: 'auto min-content',
        alignItems: 'start'
      }}
    >
      <Mask ref={MaskEl}>
        <BarChart
          colors={chartColors}
          xAxis={[
            {
              scaleType: 'band',
              data: formattedDates,
              label: 'Visits',
              position: 'bottom',
              labelStyle: {
                fill: 'white',
                textAnchor: 'middle',
                transform: `translate(${maskWidth * 0.45}px, -10px)`,
                fontSize: 17
              },
              // barGapRatio: 0.5,
              categoryGapRatio: 0.6
            } as AxisConfig<'band', string, ChartsXAxisProps>
          ]}
          yAxis={[
            {
              label: 'Pints',
              position: 'left',
              labelStyle: {
                fill: 'white',
                textAnchor: 'middle',
                transform: 'translate(10px, -80px)',
                fontSize: 17
              }
            }
          ]}
          dataset={data}
          series={[
            { dataKey: 'actual', label: 'Total actual saved' },
            { dataKey: 'total', label: 'Total possible saved' }
          ]}
          height={220}
          slotProps={{
            legend: {
              direction: 'column',
              padding: { left: 200 },
              position: { vertical: 'top', horizontal: 'right' },
              hidden: true
            }
          }}
          sx={{
            [`.MuiChartsAxis-line,
            .MuiChartsAxis-tick
          `]: {
              stroke: white ? 'white' : theme.colors.avani.turc
            },
            [`.MuiChartsAxis-tickLabel`]: {
              fill: white ? 'white' : theme.colors.avani.turc
            },
            [`.MuiBarElement-series-auto-generated-id-0`]: {
              fill: white ? customChartSeriesColor : 'url(#diagonalLineTurc)'
            }
          }}
        >
          <div style={{ minWidth: '120px', padding: '10px', textAlign: 'right' }}>
            {/* Legend will be rendered here automatically by the chart */}
            <pattern id="diagonalLineTurc" patternUnits="userSpaceOnUse" width="4" height="4">
              <path
                d="M-1,1 l2,-2
              M0,4 l4,-4
              M3,5 l2,-2"
                style={{ stroke: theme.colors.avani.turc, strokeWidth: 1 }}
              />
            </pattern>
          </div>
        </BarChart>
      </Mask>
      <Grid2
        container
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
        minWidth="180px"
        padding="10px"
        gap={1}
      >
        <Grid2 container alignItems="center" justifyContent="flex-start" gap={1}>
          <Grid2
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: customChartSeriesColor
            }}
          />
          <Grid2>
            <Typography variant={'subtitle1'} color="white">
              Total actual saved
            </Typography>
          </Grid2>
        </Grid2>
        <Grid2 container alignItems="center" justifyContent="flex-start" gap={1}>
          <Grid2
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: theme.colors.avani.teal
            }}
          />
          <Grid2>
            <Typography variant={'subtitle1'} color="white">
              Total possible saved
            </Typography>
          </Grid2>
        </Grid2>
      </Grid2>
    </div>
  );
};
